import React, { useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  FooterAction,
} from '@ohif/ui-next';

interface UploadConfirmDialogProps {
  open: boolean;
  errorMessage: string;
  /** False for a transport or server fault, where forcing answers nothing. */
  canForce: boolean;
  isResolving: boolean;
  onContinue: () => void;
  onStop: () => void;
}

export default function UploadConfirmDialog({
  open,
  errorMessage,
  canForce,
  isResolving,
  onContinue,
  onStop,
}: UploadConfirmDialogProps) {
  const continueIfEnabled = useCallback(() => {
    if (!isResolving) {
      onContinue();
    }
  }, [isResolving, onContinue]);

  const stopIfEnabled = useCallback(() => {
    if (!isResolving) {
      onStop();
    }
  }, [isResolving, onStop]);

  return (
    <Dialog
      open={open}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
    >
      <DialogContent
        unstyled
        className="w-full sm:max-w-md"
        data-cy="sycai-upload-confirm-dialog"
      >
        <DialogHeader>
          <DialogTitle>Upload Error</DialogTitle>
          <DialogDescription className="py-2 text-white">
            An error occurred during file upload:
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-white">{errorMessage}</p>
          <p className="text-white">Do you want to continue uploading?</p>
        </div>

        <FooterAction className="gap-4 py-2">
          <FooterAction.Auxiliary
            className="hover:text-foreground/60 flex-1 hover:bg-transparent aria-disabled:opacity-50"
            dataCY="sycai-upload-stop"
            onClick={stopIfEnabled}
            aria-disabled={isResolving || undefined}
          >
            Stop
          </FooterAction.Auxiliary>
          {canForce && (
            <FooterAction.Primary
              className="flex-1 text-white aria-disabled:opacity-50"
              dataCY="sycai-upload-continue"
              onClick={continueIfEnabled}
              aria-disabled={isResolving || undefined}
            >
              Continue
            </FooterAction.Primary>
          )}
        </FooterAction>
      </DialogContent>
    </Dialog>
  );
}
